import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserCog, Loader2 } from 'lucide-react';

/**
 * Super-admin one-time backfill: for every legacy user without a
 * ``created_by`` stamp, pick an admin owner so they show up under
 * the right team in the tightened Members list.
 */
export function LegacyBackfillDialog({ open, onOpenChange, members, onAssigned }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [unassigned, setUnassigned] = useState([]);
  const [picks, setPicks] = useState({});   // user_id → admin_id

  const admins = useMemo(
    () => (members || []).filter(m => m.role === 'admin' || m.role === 'super_admin'),
    [members],
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.getUnassignedMembers();
      setUnassigned(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load unassigned users');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const assignOne = async (userId) => {
    const adminId = picks[userId];
    if (!adminId) { toast.error('Choose an admin first'); return; }
    setSaving(s => ({ ...s, [userId]: true }));
    try {
      await api.assignMemberCreator(userId, adminId);
      toast.success('Owner assigned');
      setUnassigned(rows => rows.filter(u => u.id !== userId));
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally { setSaving(s => ({ ...s, [userId]: false })); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="legacy-backfill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-4 h-4" /> Assign Legacy Owners
          </DialogTitle>
          <DialogDescription>
            Pre-Feb-2026 users have no admin owner. Pick who owns each so they appear on the right admin's Members page.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : unassigned.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500" data-testid="backfill-empty">
            All users have owners. Nothing to backfill.
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto -mx-4 sm:-mx-6">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 sm:px-6 py-2">User</th>
                  <th className="text-left px-2 py-2">Role</th>
                  <th className="text-left px-2 py-2">Admin Owner</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {unassigned.map(u => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`backfill-row-${u.id}`}>
                    <td className="px-4 sm:px-6 py-2">
                      <div className="font-medium text-slate-900">{u.name || '—'}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[180px]">{u.email}</div>
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-600">{u.role}</td>
                    <td className="px-2 py-2">
                      <select
                        className="w-full max-w-[180px] rounded-sm border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs bg-white dark:bg-slate-900"
                        value={picks[u.id] || ''}
                        onChange={(e) => setPicks(p => ({ ...p, [u.id]: e.target.value }))}
                        data-testid={`backfill-select-${u.id}`}
                      >
                        <option value="">Choose admin…</option>
                        {admins.filter(a => a.id !== u.id).map(a => (
                          <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 pr-4 sm:pr-6 text-right">
                      <Button
                        size="sm"
                        disabled={!picks[u.id] || saving[u.id]}
                        onClick={() => assignOne(u.id)}
                        className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                        data-testid={`backfill-assign-${u.id}`}
                      >
                        {saving[u.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Assign'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
